import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1704564552247-398da345be3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxibGFjayUyMGFuZCUyMHdoaXRlJTIwZmFzaGlvbiUyMHJ1bndheXxlbnwwfHx8fDE3Nzk4MjkxNTN8MA&ixlib=rb-4.1.0&q=85";

export default function Hero({ onApply, onPartner }) {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-black"
    >
      {/* Background image */}
      <motion.div
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
      >
        <img
          src={HERO_IMG}
          alt="Black and white editorial fashion runway"
          className="w-full h-full object-cover grayscale"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" />
      </motion.div>

      {/* Issue / index strip */}
      <div className="absolute top-24 md:top-28 left-0 right-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/60">
          <span data-testid="hero-volume">Volume Ⅰ — Index Nº 01</span>
          <span className="hidden md:inline">New York · Lagos · Paris</span>
          <span>MMXXV</span>
        </div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-16 md:pb-24">
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-xs uppercase tracking-[0.32em] text-white/70 mb-8"
          data-testid="hero-overline"
        >
          A Venture Studio for Fashion
        </motion.p>

        <motion.h1
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[8.5rem] leading-[0.95] tracking-tight max-w-[18ch]"
          data-testid="hero-headline"
        >
          Building the <span className="italic font-light">future</span><br />
          infrastructure of <span className="italic font-light">fashion.</span>
        </motion.h1>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-8 md:mt-10 max-w-xl text-white/75 text-base md:text-lg font-light leading-relaxed"
          data-testid="hero-subheadline"
        >
          Runway Foundry incubates and supports technology-enabled businesses
          redefining how fashion is built, distributed, and experienced.
        </motion.p>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10 md:mt-14 flex flex-col sm:flex-row gap-4 sm:gap-5"
        >
          <button
            data-testid="hero-apply-button"
            onClick={onApply}
            className="group inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-white/90 transition-all"
          >
            Apply as a Founder
            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
          </button>
          <button
            data-testid="hero-partner-button"
            onClick={onPartner}
            className="group inline-flex items-center justify-center gap-3 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:border-white hover:bg-white/5 transition-all"
          >
            Partner With Us
            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
          </button>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 right-6 md:right-10 z-10 text-[10px] uppercase tracking-[0.3em] text-white/50 flex items-center gap-3"
      >
        <span className="hidden sm:inline">Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
}
